import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { DriverSyncStatus, isBusyDriver, TripIntegrationDetails } from 'components/Erp/IntegrationTrip';
import React, { useState, useEffect } from "react";
import { DateTime, Vehicle, StatusBadge as SharedStatusBadge } from "components/Erp/Display";
import { SectionHeading } from "components/Erp/Presentation";
import DriverBlockReason from 'components/Erp/DriverBlockReason/DriverBlockReason.tsx';
import {
  Phone,
  UserRound,
  Paperclip,
  FileText,
  Folder,
  ExternalLink,
  X,
} from "lucide-react";
import s from "./DriverInfoCard.module.scss";
import { getDriverDocuments, getDriverDetail } from "service/AdminPanelService";

const DOC_LABELS = [
  "Фото ТС",
  "Паспорт (л)",
  "Паспорт (о)",
  "Права (л)",
  "Права (о)",
  "Тех.пасп. (л)",
  "Тех.пасп. (о)",
];
const DOC_FIELDS = [
  "photo_vehicle",
  "passport_front",
  "passport_back",
  "license_front",
  "license_back",
  "tech_passport_front",
  "tech_passport_back",
];

const PhoneLink = ({ phone }) => {
  useInterfaceLanguage();
  if (!phone || phone === "-") return <span>—</span>;
  return (
    <a href={`tel:${phone}`} className={s.phoneLink}>
      <Phone size={13} /> {tr(phone)}
    </a>
  );
};

const StatusBadge = ({ label, colorKey }) => { useInterfaceLanguage(); return (<SharedStatusBadge tone={({ active: 'success', free: 'success', inactive: 'danger', blocked: 'danger', route: 'info', loading: 'warning' })[colorKey] || 'neutral'}>{tr(label)}</SharedStatusBadge>); };

const InfoItem = ({ label, children }) => { useInterfaceLanguage(); return (<div className={s.infoItem}>
    <span className={s.infoLabel}>{tr(label)}</span>
    <div className={s.infoValue}>{children}</div>
  </div>); };

const getVehicleColorKey = (d) => {
  if (d.status === "blocked" || d.status === "Заблокирован") return "blocked";
  if (d.status === "in_route" || d.status === "В пути") return "route";
  if (d.status === "in_loading" || isBusyDriver(d.status)) return "loading";
  return "free";
};

/**
 * DriverInfoCard — shared driver detail card
 *
 * Props:
 *   driver  — driver object (fields: employee_name, employee_phone, phone,
 *             vehicle_number, vehicle_model, uid, is_active, status,
 *             status_display, created_at, loads_count, documents[])
 */
const DriverInfoCard = ({ driver }) => {
  useInterfaceLanguage();
  const [docPreview, setDocPreview] = useState(null);
  const [docs, setDocs] = useState(null); // null = not loaded yet
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [detailedDriver, setDetailedDriver] = useState(null);

  useEffect(() => {
    if (!driver?.id) return;
    let cancelled = false;
    setDocPreview(null);
    setDetailedDriver(null);
    setDocs(Array.isArray(driver.documents) ? driver.documents : []);
    setDocsError(null);
    const complete = DOC_FIELDS.every(
      (field) =>
        Object.prototype.hasOwnProperty.call(driver, field) ||
        Object.prototype.hasOwnProperty.call(driver, field + "_url"),
    );
    const jobs = [];
    if (!complete)
      jobs.push(
        getDriverDetail(driver.id).then((res) => {
          if (!cancelled) setDetailedDriver(res.data);
        }),
      );
    if (!Array.isArray(driver.documents))
      jobs.push(
        getDriverDocuments(driver.id).then((res) => {
          if (!cancelled) setDocs(res.data.results || res.data || []);
        }),
      );
    setDocsLoading(jobs.length > 0);
    Promise.allSettled(jobs).then((results) => {
      if (!cancelled) {
        if (results.some((result) => result.status === "rejected"))
          setDocsError(
            tr("Часть документов не удалось загрузить. Доступные файлы показаны ниже."),
          );
        setDocsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [driver]);

  if (!driver) return null;

  const source =
    detailedDriver?.id === driver.id
      ? { ...driver, ...detailedDriver }
      : driver;
  const namedDocs = DOC_FIELDS.map((field, index) => {
    const file = source[field + "_url"] || source[field];
    const url = typeof file === "string" ? file : file?.url;
    return url
      ? { id: "named-" + field, description: DOC_LABELS[index], file_url: url }
      : null;
  }).filter(Boolean);
  const resolvedDocs = [
    ...(docs ?? []).filter(
      (doc) =>
        !namedDocs.some(
          (named) =>
            named.description === doc.description ||
            named.file_url === (doc.file_url || doc.file),
        ),
    ),
    ...namedDocs,
  ];
  const byDesc = {};
  resolvedDocs.forEach((d) => {
    if (d.description) byDesc[d.description] = d;
  });
  const otherDocs = resolvedDocs.filter(
    (d) => !DOC_LABELS.includes(d.description),
  );

  return (
    <div className={s.card}>
      {/* ── Информация о водителе ── */}
      <div className={s.section}>
        <SectionHeading as="h3" icon={UserRound} title={tr("Информация о водителе")} />
        <div className={s.infoGrid}>
          <InfoItem label={tr("ФИО")}>{tr(driver.employee_name || "—")}</InfoItem>
          <InfoItem label={tr('ИНН')}>{source.inn || '—'}</InfoItem>
          <InfoItem label={tr("Телефон")}>
            <PhoneLink phone={driver.employee_phone} />
          </InfoItem>
          <InfoItem label={tr("Доп. телефон")}>
            <PhoneLink phone={driver.phone} />
          </InfoItem>
          <InfoItem label={tr("Гос. номер")}>
            <Vehicle number={driver.vehicle_number} />
          </InfoItem>
          <InfoItem label={tr("Модель")}>{tr(driver.vehicle_model || "—")}</InfoItem>
          {tr(driver.uid && (
            <InfoItem label={tr("UID")}>
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                {tr(driver.uid)}
              </span>
            </InfoItem>
          ))}
          {tr(driver.loads_count !== undefined && (
            <InfoItem label={tr("Погрузок")}>
              <strong style={{ color: "var(--theme-primary-text, #0d00f3)" }}>{tr(driver.loads_count)}</strong>
            </InfoItem>
          ))}
          <InfoItem label={tr("Синхронизация")}><DriverSyncStatus driver={source} /></InfoItem><InfoItem label={tr("Активность")}>
            <StatusBadge
              label={driver.is_active ? tr("Активен") : tr("Неактивен")}
              colorKey={driver.is_active ? "active" : "inactive"}
            />
          </InfoItem>
          <InfoItem label={tr("Статус машины")}>
            <StatusBadge
              label={driver.status_display || driver.status || tr("Свободен")}
              colorKey={getVehicleColorKey(driver)}
            />
          </InfoItem>
          {tr(driver.created_at && (
            <InfoItem label={tr("Зарегистрирован")}>
              <DateTime value={driver.created_at} />
            </InfoItem>
          ))}
        </div>
        {(source.status === 'Заблокирован' || source.status === 'blocked') && <DriverBlockReason reason={source.blocked_reason} />}
      </div>

      {tr(source.integration_active_load && <div className={s.section}><TripIntegrationDetails load={source.integration_active_load} compact /></div>)}

      {/* ── Документы водителя ── */}
      <div className={s.section}>
        <SectionHeading as="h3" icon={Paperclip} title={tr("Документы водителя")} tone="blue" />
        {docsLoading ? (
          <div className={s.docsEmpty}>{tr("Загрузка документов…")}</div>
        ) : (
          <>
            {tr(docsError && (
              <div
                role="alert"
                style={{ padding: "10px 0", fontSize: 13, color: "var(--theme-danger-text, #b73030)" }}
              >
                {tr(docsError)}
              </div>
            ))}
            <div className={s.docsGrid}>
              {DOC_LABELS.map((label) => {
                const doc = byDesc[label];
                const fileUrl = doc ? doc.file_url || doc.file : null;
                const isActive = docPreview?.url === fileUrl && fileUrl;
                return (
                  <button
                    type="button"
                    key={label}
                    className={`${s.docBtn} ${
                      doc ? s.docBtnHas : s.docBtnMissing
                    } ${isActive ? s.docBtnActive : ""}`}
                    onClick={() => {
                      if (!fileUrl) return;
                      setDocPreview(isActive ? null : { url: fileUrl, label });
                    }}
                    title={
                      doc ? tr("Открыть: {{value0}}", { value0: label }) : tr("{{value0}} — не загружено", { value0: label })
                    }
                  >
                    <span className={s.docBtnIcon}>
                      {doc ? <FileText size={15} /> : "—"}
                    </span>
                    <span className={s.docBtnLabel}>{tr(label)}</span>
                  </button>
                );
              })}
              {otherDocs.map((doc) => {
                const fileUrl = doc.file_url || doc.file;
                const isActive = docPreview?.url === fileUrl;
                return (
                  <button
                    type="button"
                    key={doc.id}
                    className={`${s.docBtn} ${s.docBtnHas} ${
                      isActive ? s.docBtnActive : ""
                    }`}
                    onClick={() =>
                      setDocPreview(
                        isActive
                          ? null
                          : {
                              url: fileUrl,
                              label: doc.description || doc.file_name || tr("Файл"),
                            },
                      )
                    }
                  >
                    <span className={s.docBtnIcon}>
                      <Folder size={15} />
                    </span>
                    <span className={s.docBtnLabel}>
                      {tr(doc.description || doc.file_name || tr("Файл"))}
                    </span>
                  </button>
                );
              })}
              {tr(!resolvedDocs.length && (
                <div className={s.docsEmpty}>{tr("Документы не загружены")}</div>
              ))}
            </div>

            {tr(docPreview && (
              <div className={s.docPreview}>
                <div className={s.docPreviewHeader}>
                  <div className={s.docPreviewTitle}>
                    <FileText size={15} />
                    <span>{tr(docPreview.label)}</span>
                  </div>
                  <div
                    style={{ display: "flex", gap: 6, alignItems: "center" }}
                  >
                    <a
                      href={docPreview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={s.docPreviewOpen}
                    >
                      <ExternalLink size={13} />{tr(" Открыть")}</a>
                    <button
                      type="button"
                      className={s.docPreviewClose}
                      aria-label={tr("Закрыть документ")}
                      onClick={() => setDocPreview(null)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
                <div className={s.docPreviewBody}>
                  {String(docPreview.url).toLowerCase().includes(".pdf") ? (
                    <iframe
                      src={docPreview.url}
                      title={tr(docPreview.label)}
                      className={s.docPreviewPdf}
                    />
                  ) : <img
                    key={docPreview.url}
                    src={docPreview.url}
                    alt={tr(docPreview.label)}
                    className={s.docPreviewImg}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />}
                  <div
                    style={{
                      display: "none",
                      padding: "24px",
                      textAlign: "center",
                    }}
                  >
                    <a
                      href={docPreview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={s.docPreviewOpen}
                    >{tr("Скачать файл")}</a>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default DriverInfoCard;
